'use client';

import * as React from 'react';
import { Plus } from '@phosphor-icons/react';
import { cn, formatPrice } from '@/lib/utils';
import { ThemedImage } from '@/components/ui/themed-image';
import type { ProductWithCategories } from '@/lib/api/products';

export interface SetOfTheDayCardProps {
  product: ProductWithCategories;
  onClick?: (product: ProductWithCategories) => void;
  className?: string;
  /**
   * Компактный режим — баннер занимает половину ширины (используется парой
   * сетов дня на ПК). Уменьшает шрифты, padding, снимает ограничение max-width
   * и отключает desktop-видео в фоне (2 одновременных видео = ненужная нагрузка).
   * На мобиле compact игнорируется — там carousel показывает по одному за раз.
   */
  compact?: boolean;
}

const RIBBON_GRADIENT = 'from-[#EC4899] to-[#F472B6]';
const RIBBON_SHADOW = 'rgba(244,114,182,0.4)';

function pad2(n: number) {
  return n.toString().padStart(2, '0');
}

// Ближайший четверг 00:00 по Москве (UTC+3, без перехода на летнее время)
function getNextThursdayMidnightMskMs(from: number = Date.now()): number {
  const MSK_OFFSET_MS = 3 * 3600000;
  // Сдвигаем на +3ч чтобы через UTC-методы читать "МСК-день"
  const mskNow = new Date(from + MSK_OFFSET_MS);
  const mskDay = mskNow.getUTCDay(); // 0=Вс, 4=Чт
  let daysUntil = (4 - mskDay + 7) % 7;
  if (daysUntil === 0) daysUntil = 7; // если уже четверг по МСК — следующий через неделю
  // Полночь четверга в "МСК-координатах" (представленная через UTC)
  const mskThuMidnight = Date.UTC(
    mskNow.getUTCFullYear(),
    mskNow.getUTCMonth(),
    mskNow.getUTCDate() + daysUntil,
    0, 0, 0, 0
  );
  // Обратно из МСК в UTC
  return mskThuMidnight - MSK_OFFSET_MS;
}

function useCountdown(targetMs: number) {
  // ВАЖНО: НЕ инициализировать через Date.now() — это вызывает hydration mismatch
  // (на сервере и клиенте время отличается на сотни мс → таймер показывает
  // разные секунды → React error #418). Начинаем с 0 → на сервере все таймеры
  // отрисуются одинаково (`00:00:00`), на клиенте обновятся после mount.
  const [now, setNow] = React.useState<number>(0);
  React.useEffect(() => {
    setNow(Date.now()); // первое реальное значение — только в браузере
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  if (now === 0) {
    // До mount показываем нули — детерминированно, без mismatch
    return { h: 0, m: 0, s: 0 };
  }
  const diff = Math.max(0, targetMs - now);
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return { h, m, s };
}

const SetOfTheDayCardInner: React.FC<SetOfTheDayCardProps> = ({ product, onClick, className, compact = false }) => {
  const desktopSrc = product.set_of_the_day_image_desktop || product.image_url || '';
  const mobileSrc = product.set_of_the_day_image_mobile || product.image_url || '';
  // В compact режиме на ПК видео не используем — только статика.
  const desktopVideo = compact ? null : (product.set_of_the_day_video_desktop || null);
  const mobileVideo = product.set_of_the_day_video_mobile || null;

  // Обратный отсчёт до ближайшего четверга 00:00 МСК.
  // targetMs тоже инициализируем после mount — иначе server и client дают разные ms
  // (разница может быть = 1 секунде, что меняет каждый таймер).
  const [targetMs, setTargetMs] = React.useState<number>(0);
  React.useEffect(() => {
    setTargetMs(getNextThursdayMidnightMskMs());
  }, []);
  const { h, m, s } = useCountdown(targetMs);
  const countdownLabel = `${pad2(h)}:${pad2(m)}:${pad2(s)}`;

  const handleClick = React.useCallback(() => {
    onClick?.(product);
  }, [onClick, product]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick?.(product);
      }
    },
    [onClick, product]
  );

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={cn(
        'group relative isolate w-full overflow-hidden cursor-pointer select-none',
        // На ПК это крупная секция во всю ширину — ступень screen (32).
        // На мобиле баннер живёт в потоке карточек, поэтому card (20).
        'rounded-card md:rounded-screen',
        'aspect-[5/4] md:aspect-auto md:h-[370px]',
        'bg-[var(--surface-secondary)]',
        'shadow-float',
        'transition-[transform,box-shadow] duration-300 ease-out',
        // Фиолетовый ореол при наведении СОХРАНЁН (ответ 35 = Б) — он и был
        // здесь фирменной чертой, просто набран вручную. Теперь это роль.
        'hover:shadow-glow-lg',
        'active:scale-[0.99]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)] focus-visible:ring-offset-2',
        className
      )}
    >
      {/* Background media — desktop (видео если задано, иначе картинка) */}
      <div className="absolute inset-0 hidden md:block">
        {desktopVideo ? (
          <video
            src={desktopVideo}
            poster={desktopSrc || undefined}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <ThemedImage
            src={desktopSrc}
            alt={product.name}
            fill
            priority
            className="object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.03]"
          />
        )}
      </div>

      {/* Background media — mobile (видео если задано, иначе картинка) */}
      <div className="absolute inset-0 md:hidden">
        {mobileVideo ? (
          <video
            src={mobileVideo}
            poster={mobileSrc || undefined}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <ThemedImage
            src={mobileSrc}
            alt={product.name}
            fill
            priority
            className="object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.03]"
          />
        )}
      </div>

      {/* Бейдж "СЕТ ДНЯ" — в стиле corner-ribbon.popular */}
      <div
        className={cn(
          'absolute top-0 left-0 z-10',
          'rounded-br-card',
          'bg-gradient-to-r text-white font-bold uppercase tracking-wider',
          'text-[10px] md:text-[11px]',
          'px-3 py-1.5 md:px-4 md:py-2',
          'pointer-events-none select-none',
          RIBBON_GRADIENT
        )}
        style={{ boxShadow: `2px 2px 12px ${RIBBON_SHADOW}` }}
      >
        <span
          aria-hidden
          className="absolute inset-x-[8%] top-[1px] h-[40%] rounded-pill bg-white/25"
        />
        <span className="relative z-[1]">
          Сет дня
          <span className="mx-1.5 opacity-70">·</span>
          <span className="tabular-nums">{countdownLabel}</span>
        </span>
      </div>

      {/* Плюс — в правом верхнем углу */}
      <span
        aria-label="Подробнее"
        className={cn(
          'absolute top-3 right-3 md:top-5 md:right-5 z-10',
          'inline-flex items-center justify-center shrink-0',
          'h-10 w-10 md:h-12 md:w-12 rounded-pill',
          'bg-white text-[var(--primary)]',
          'shadow-float',
          'transition-all duration-200',
          'group-hover:scale-[1.06] group-hover:bg-[var(--primary)] group-hover:text-white',
          'group-active:scale-95'
        )}
      >
        <Plus size={22} weight="bold" />
      </span>

      {/* Контент: название + цена */}
      <div
        className={cn(
          'absolute inset-x-0 top-0 z-10',
          'md:inset-y-0 md:justify-center',
          'flex flex-col gap-1 md:gap-4',
          'pt-[34px] px-5 pb-5',
          compact ? 'md:p-6 lg:p-8' : 'md:p-9 lg:p-12',
          // На compact-баннере (половина ширины) тексту нужно больше места —
          // снимаем строгое ограничение, иначе заголовок и цена теснятся.
          compact ? 'md:max-w-[80%] lg:max-w-[78%]' : 'md:max-w-[58%] lg:max-w-[52%]',
          'text-white'
        )}
      >
        {/* Мини-подпись над названием (только ПК) */}
        <span
          className={cn(
            'hidden md:inline-block',
            'font-semibold uppercase tracking-[0.2em]',
            compact ? 'text-[10px]' : 'text-[11px]',
            'text-white/85',
            'drop-shadow-[0_1px_6px_rgba(0,0,0,0.4)]'
          )}
        >
          Специальное предложение
        </span>

        <h3
          className={cn(
            'font-heading font-bold tracking-tight leading-[1.05]',
            'text-2xl',
            compact ? 'md:text-3xl lg:text-4xl' : 'md:text-4xl lg:text-5xl',
            'text-white',
            'drop-shadow-[0_2px_10px_rgba(0,0,0,0.45)]',
            'text-balance'
          )}
        >
          {product.name}
        </h3>

        <span
          className={cn(
            'font-bold tabular-nums leading-none',
            'text-3xl',
            compact ? 'md:text-4xl lg:text-5xl' : 'md:text-5xl lg:text-[56px]',
            'text-white',
            'drop-shadow-[0_2px_10px_rgba(0,0,0,0.35)]'
          )}
        >
          {formatPrice(product.price)}
        </span>
      </div>
    </div>
  );
};

export const SetOfTheDayCard = React.memo(SetOfTheDayCardInner);
SetOfTheDayCard.displayName = 'SetOfTheDayCard';
